/**
 * Worker message protocol — the single source of truth for main↔worker
 * messages. Mirrors python/webbridge/worker_main.py; keep the two in sync.
 *
 * Wire rules: JSON objects only; no 64-bit integers (Zobrist keys stay on the
 * Python side). Board cells are packed small ints: rank (1-8) | side<<4
 * (0 = RED, 1 = BLUE), or null for empty. Positions are [row, col] pairs.
 */

export type Side = 'RED' | 'BLUE';
export type Strength = 'easy' | 'medium' | 'hard';
export type GameMode = 'human_vs_ai' | 'ai_vs_ai';
export type Pos = [row: number, col: number];

export interface SearchBudget {
  depth: number;
  soft: number;
  hard: number;
}

// -- main → worker -----------------------------------------------------------

interface BaseRequest {
  id?: number;
  type: string;
}

export interface InitRequest extends BaseRequest {
  type: 'init';
  engineZipUrl: string;
  manifestUrl: string;
  pyodideBaseUrl: string;
}

export interface SelfTestRequest extends BaseRequest {
  type: 'self_test';
  manifest: EngineManifest;
}

export interface NewGameRequest extends BaseRequest {
  type: 'new_game';
  mode: GameMode;
  humanFirst: boolean;
  strength: Strength;
  /** Test/debug override for the strength budget (desktop --depth/--time-limit). */
  budget?: SearchBudget;
}

export interface HumanMoveRequest extends BaseRequest {
  type: 'human_move';
  from: Pos;
  to: Pos;
}

export interface SimpleRequest extends BaseRequest {
  type: 'request_ai_move' | 'undo' | 'abort_ai';
}

export interface SetStrengthRequest extends BaseRequest {
  type: 'set_strength';
  strength: Strength;
}

export type WorkerRequest =
  | InitRequest
  | SelfTestRequest
  | NewGameRequest
  | HumanMoveRequest
  | SimpleRequest
  | SetStrengthRequest;

// -- worker → main -----------------------------------------------------------

export interface MoveRef {
  from: Pos;
  to: Pos;
  capture: boolean;
}

export interface GameOverInfo {
  winner: Side | null;
  draw: boolean;
  reason: 'den' | 'elimination' | 'no_moves' | 'move_limit' | 'repetition' | string;
}

export interface MoveLogEntry {
  ply: number;
  side: Side;
  rank: string;
  from: Pos;
  to: Pos;
  capture: boolean;
  notation: string;
}

export interface StateSnapshot {
  cells: (number | null)[];
  sideToMove: Side;
  moveCount: number;
  legalMoves: [number, number, number, number][];
  lastMove: MoveRef | null;
  gameOver: GameOverInfo | null;
  canUndo: boolean;
  moveLog: MoveLogEntry[];
  mode: GameMode;
  humanSide: Side | null;
  strength: Strength;
}

export interface StateMessage extends StateSnapshot {
  type: 'state';
  id?: number;
}

export interface AiMoveMessage extends StateSnapshot {
  type: 'ai_move';
  id?: number;
  move: { from: Pos; to: Pos };
  search: { depth: number; score: number; nodes: number; elapsedMs: number };
}

export interface ReadyMessage {
  type: 'ready';
  id?: number;
  engineVersion: string;
  pyodideVersion: string;
  sab: boolean;
}

export interface ProgressMessage {
  type: 'init_progress';
  stage: 'download' | 'python' | 'engine' | 'selftest';
  detail?: string;
}

export interface SearchInfoMessage {
  type: 'search_info';
  depth: number;
  score: number;
  nodes: number;
}

export interface MoveRejectedMessage {
  type: 'move_rejected';
  id?: number;
  reason: string;
}

export interface OkMessage {
  type: 'ok';
  id?: number;
}

export interface ErrorMessage {
  type: 'error';
  id?: number;
  message: string;
  fatal: boolean;
}

export interface SelfTestResultMessage {
  type: 'self_test_result';
  id?: number;
  ok: boolean;
  checks: Record<string, boolean>;
  engineVersion: string;
  protocolVersion: number;
}

export type WorkerResponse =
  | StateMessage
  | AiMoveMessage
  | ReadyMessage
  | ProgressMessage
  | SearchInfoMessage
  | MoveRejectedMessage
  | OkMessage
  | ErrorMessage
  | SelfTestResultMessage;

// -- engine bundle manifest ----------------------------------------------------

export interface EngineManifest {
  version: string;
  files: Record<string, string>;
  goldens: {
    startKeyHex: string;
    perftModel: Record<string, number>;
    perftCore: Record<string, number>;
  };
}

// -- cell packing helpers --------------------------------------------------------

export const RANKS = [null, 'Rat', 'Cat', 'Dog', 'Wolf', 'Leopard', 'Tiger', 'Lion', 'Elephant'] as const;

export function cellRank(cell: number): number {
  return cell & 15;
}

export function cellSide(cell: number): Side {
  return ((cell >> 4) & 1) === 0 ? 'RED' : 'BLUE';
}

// -- runtime validation ------------------------------------------------------------

const SIDES: readonly string[] = ['RED', 'BLUE'];

function isPos(value: unknown): value is Pos {
  return Array.isArray(value) && value.length === 2
    && Number.isInteger(value[0]) && Number.isInteger(value[1])
    && value[0] >= 0 && value[0] < 9 && value[1] >= 0 && value[1] < 7;
}

function isMoveRef(value: unknown): value is MoveRef {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return isPos(v.from) && isPos(v.to) && typeof v.capture === 'boolean';
}

/** Validate a state snapshot from the worker. Throws on any protocol drift. */
export function validateSnapshot(msg: unknown): asserts msg is StateMessage {
  if (typeof msg !== 'object' || msg === null) throw new Error('snapshot: not an object');
  const m = msg as Record<string, unknown>;
  if (!Array.isArray(m.cells) || m.cells.length !== 63) {
    throw new Error('snapshot: cells must have 63 entries');
  }
  for (const cell of m.cells) {
    if (cell !== null && (!Number.isInteger(cell) || cell < 1 || cell > 24)) {
      throw new Error(`snapshot: bad packed cell ${cell}`);
    }
  }
  if (!SIDES.includes(m.sideToMove as string)) throw new Error('snapshot: bad sideToMove');
  if (!Number.isInteger(m.moveCount)) throw new Error('snapshot: bad moveCount');
  if (!Array.isArray(m.legalMoves)) throw new Error('snapshot: bad legalMoves');
  for (const lm of m.legalMoves as unknown[]) {
    if (!Array.isArray(lm) || lm.length !== 4 || !lm.every((n) => Number.isInteger(n))) {
      throw new Error('snapshot: bad legal move entry');
    }
  }
  if (m.lastMove !== null && !isMoveRef(m.lastMove)) throw new Error('snapshot: bad lastMove');
  if (m.gameOver !== null) {
    const go = m.gameOver as Record<string, unknown>;
    if (typeof go !== 'object' || (go.winner !== null && !SIDES.includes(go.winner as string))
      || typeof go.draw !== 'boolean' || typeof go.reason !== 'string') {
      throw new Error('snapshot: bad gameOver');
    }
  }
  if (typeof m.canUndo !== 'boolean') throw new Error('snapshot: bad canUndo');
  if (!Array.isArray(m.moveLog)) throw new Error('snapshot: bad moveLog');
  if (m.humanSide !== null && !SIDES.includes(m.humanSide as string)) {
    throw new Error('snapshot: bad humanSide');
  }
}

/** Narrow a raw worker message to its response kind (lightweight, additive). */
export function responseKind(raw: unknown): WorkerResponse['type'] {
  if (typeof raw !== 'object' || raw === null || typeof (raw as { type?: unknown }).type !== 'string') {
    throw new Error('worker message without a type');
  }
  return (raw as WorkerResponse).type;
}
